import React, { useEffect, useState } from 'react';
import { getRequest, deleteRequest } from '../../apis/ApiInstence';
import axios from 'axios';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';

const ListNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState({
    open: false,
    title: '',
    description: ''
  });

  const [formData, setFormData] = useState({
    news_title: '',
    description: '',
    category: '',
    state: '',
    language: '',
    youtube_url: '',
    reporter_name: '',
    thumbnail: null,
    reporter_image: null,
    existingImage: '',
    existingReporterImage: '',
    is_active: 1
  });

  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await getRequest('product');

      if (Array.isArray(res?.data)) {
        setNewsList(res.data);
      } else {
        setNewsList([]);
        toast.error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Fetch failed:', error);
      toast.error('❌ Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleRemove = async (id) => {
    if (!window.confirm('Delete this news?')) return;

    try {
      await deleteRequest(`product/${id}`);
      toast.success('🗑️ News deleted!');
      fetchNews();
    } catch (err) {
      console.error(err);
      toast.error('❌ Failed to delete news');
    }
  };

  const handleEditClick = (news) => {
    setSelectedNews(news);

    setFormData({
      news_title: news.news_title || '',
      description: news.description || '',
      category: news.category || '',
      state: news.state || '',
      language: news.language || '',
      youtube_url: news.youtube_url || '',
      reporter_name: news.reporter_name || '',
      thumbnail: null,
      reporter_image: null,
      existingImage: news.thumbnail
        ? `${process.env.REACT_APP_BACKEND}uploads/news/${news.thumbnail}`
        : '',
      existingReporterImage: news.reporter_image
        ? `${process.env.REACT_APP_BACKEND}uploads/news/${news.reporter_image}`
        : '',
      is_active: news.is_active ?? 1
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };
  const openDescriptionModal = (title, description) => {
    setDescriptionModal({
      open: true,
      title: title || 'News Description',
      description: description || '-'
    });
  };
  const closeDescriptionModal = () => {
    setDescriptionModal({
      open: false,
      title: '',
      description: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, type, value, files, checked } = e.target;

    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveChanges = async () => {
    const updatedData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'thumbnail' || key === 'reporter_image') {
        if (value) updatedData.append(key, value);
      } else if (!key.startsWith('existing')) {
        updatedData.append(key, value);
      }
    });

    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/product/${selectedNews.id}`,
        updatedData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('News updated successfully');
      fetchNews();
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update news');
    }
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    {
      Header: 'Image',
      accessor: 'thumbnail',
      Cell: ({ row }) =>
        row.original.thumbnail ? (
          <img
            src={`${process.env.REACT_APP_BACKEND}uploads/news/${row.original.thumbnail}`}
            alt={row.original.news_title}
            width={100}
          />
        ) : 'No Image'
    },
    { Header: 'Reporter', accessor: 'reporter_name' },
    {
      Header: 'Reporter Image',
      accessor: 'reporter_image',
      Cell: ({ row }) =>
        row.original.reporter_image ? (
          <img
            src={`${process.env.REACT_APP_BACKEND}uploads/news/${row.original.reporter_image}`}
            alt="reporter"
            width={60}
            style={{ borderRadius: '50%' }}
          />
        ) : '-'
    },
    { Header: 'Title', accessor: 'news_title' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => {
        const fullText = row.original.description || '-';
        const maxLength = 50;
        const isLong = fullText.length > maxLength;
        const shortText = isLong ? `${fullText.slice(0, maxLength)}...` : fullText;

        return (
          <span>
            {shortText}
            {isLong ? (
              <button
                className="btn btn-link p-0 ml-1"
                style={{ textDecoration: 'underline' }}
                onClick={() => openDescriptionModal(row.original.news_title, fullText)}
              >
                Show more
              </button>
            ) : null}
          </span>
        );
      }
    },
    { Header: 'Category', accessor: 'category' },
    { Header: 'State', accessor: 'state' },
    { Header: 'Language', accessor: 'language' },
    {
      Header: 'YouTube',
      accessor: 'youtube_url',
      Cell: ({ row }) =>
        row.original.youtube_url ? (
          <a href={row.original.youtube_url} target="_blank" rel="noreferrer">
            View
          </a>
        ) : '-'
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span className={row.original.is_active ? 'badge bg-success' : 'badge bg-danger'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-danger mx-1" onClick={() => handleRemove(row.original.id)}>
            Remove
          </button>

          <button className="btn btn-primary mx-1" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-4 mt-md-5 ournews-page">
        <div className="mainheadig mx-2 mx-md-4">
          <h4 className="text-white font-weight-bold">News List</h4>
        </div>

        {loading && <p className="text-white mx-2 mx-md-4">Loading...</p>}
        {!loading && newsList.length === 0 && <p className="text-white mx-2 mx-md-4">No news found.</p>}

        {!loading && newsList.length > 0 ? (
          <>
            <div className="d-none d-md-block">
              <AdminTable tableHeaders={tableHeaders} tableData={newsList} />
            </div>

            <div className="d-md-none px-2">
              {newsList.map((item) => (
                <div key={item.id} className="card mb-3 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong>#{item.id}</strong>
                      <span className={item.is_active ? 'badge bg-success text-white' : 'badge bg-danger text-white'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <h6 className="mb-2">{item.news_title || '-'}</h6>

                    {item.thumbnail ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND}uploads/news/${item.thumbnail}`}
                        alt={item.news_title}
                        className="img-fluid rounded mb-2 ournews-mobile-thumb"
                      />
                    ) : null}

                    <p className="mb-1">
                      <strong>Reporter:</strong> {item.reporter_name || '-'}
                    </p>

                    {item.reporter_image ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND}uploads/news/${item.reporter_image}`}
                        alt="reporter"
                        width={48}
                        height={48}
                        className="rounded-circle mb-2"
                        style={{ objectFit: 'cover' }}
                      />
                    ) : null}

                    <p className="mb-1">
                      <strong>Category:</strong> {item.category || '-'}
                    </p>
                    <p className="mb-1">
                      <strong>State:</strong> {item.state || '-'}
                    </p>
                    <p className="mb-1">
                      <strong>Language:</strong> {item.language || '-'}
                    </p>
                    {item.youtube_url ? (
                      <p className="mb-2">
                        <strong>YouTube:</strong>{' '}
                        <a href={item.youtube_url} target="_blank" rel="noreferrer">
                          View
                        </a>
                      </p>
                    ) : null}

                    <p className="mb-2">
                      <strong>Description:</strong>{' '}
                      {(item.description || '-').length > 80 ? `${item.description.slice(0, 80)}...` : item.description || '-'}
                      {(item.description || '').length > 80 ? (
                        <button
                          className="btn btn-link p-0 ml-1"
                          style={{ textDecoration: 'underline' }}
                          onClick={() => openDescriptionModal(item.news_title, item.description)}
                        >
                          Show more
                        </button>
                      ) : null}
                    </p>

                    <div className="d-flex flex-column" style={{ gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => handleEditClick(item)}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleRemove(item.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      {showModal && selectedNews && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg ournews-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit News</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>

              <div className="modal-body">
                <div className="row">
                  <div className="col-12 col-md-6 mb-2">
                    <input name="news_title" value={formData.news_title} onChange={handleFormChange} placeholder="Title" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input
                      name="reporter_name"
                      value={formData.reporter_name}
                      onChange={handleFormChange}
                      placeholder="Reporter Name"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="category" value={formData.category} onChange={handleFormChange} placeholder="Category" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="state" value={formData.state} onChange={handleFormChange} placeholder="State" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input name="language" value={formData.language} onChange={handleFormChange} placeholder="Language" className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input
                      name="youtube_url"
                      value={formData.youtube_url}
                      onChange={handleFormChange}
                      placeholder="YouTube URL"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 mb-2">
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Description"
                      className="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input type="file" name="thumbnail" onChange={handleFormChange} className="form-control" />
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <input type="file" name="reporter_image" onChange={handleFormChange} className="form-control" />
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 10 }}>
                  {formData.thumbnail ? (
                    <img src={URL.createObjectURL(formData.thumbnail)} width={120} alt="preview" className="img-fluid rounded" />
                  ) : formData.existingImage ? (
                    <img src={formData.existingImage} width={120} alt="existing" className="img-fluid rounded" />
                  ) : null}

                  {formData.reporter_image ? (
                    <img
                      src={URL.createObjectURL(formData.reporter_image)}
                      width={80}
                      height={80}
                      alt="preview"
                      style={{ borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : formData.existingReporterImage ? (
                    <img
                      src={formData.existingReporterImage}
                      width={80}
                      height={80}
                      alt="existing"
                      style={{ borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : null}
                </div>

                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active === 1}
                    onChange={handleFormChange}
                    className="form-check-input"
                  />
                  <label className="form-check-label" htmlFor="is_active">
                    Active
                  </label>
                </div>

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveChanges}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {descriptionModal.open ? (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered ournews-desc-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5>{descriptionModal.title}</h5>
                <button type="button" className="close" onClick={closeDescriptionModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{descriptionModal.description}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDescriptionModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ListNews;
